/**
 * 报价单 (票据样式, 数据整理对齐旧版 index.html formatQuoteMessage):
 *  - 抬头: 「报价单」+ 时间 + 「复制报价」(纯文本写入剪贴板)
 *  - 明细行: 品名 · 规格 · 数量 · 单价, 小计右对齐 (tabular-nums)
 *  - calculation_details 按 query_index 分为「询价 N」小标题, 每组有小计
 *  - 合计: 琥珀色金额 (--price), 大号
 *  - 识别参数 / 计算过程: 可折叠
 * 只在报价完成的轮次渲染 (追问轮次只有气泡)。
 */
import { useState, type ReactNode } from "react";
import { Calculator, Check, ChevronDown, Copy, ReceiptText, ScanSearch } from "lucide-react";
import { Badge, Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { copyWithToast } from "../clipboard";
import {
  detailSpec,
  fmtMoney as fmt,
  groupByQuery,
  groupSubtotal,
  paramSpec,
  productTypeName,
  quantityUnit,
  quoteToPlainText,
  type CalcDetail,
  type ExtractedParam,
} from "../quoteFormat";

interface Props {
  details: CalcDetail[];
  totalPrice: number;
  extractedParams?: ExtractedParam[];
  /** 报价时间 (ISO), 显示在抬头 */
  time?: string;
}

/** "2026-10-04T10:00:03" → "2026-10-04 10:00" */
function fmtTime(ts?: string): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts.replace("T", " ").slice(0, 16);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 桌面端明细的列: 品名 | 规格 | 数量 | 单价 | 小计 */
const COLS = "sm:grid sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1.3fr)_4.5rem_5rem_5.5rem] sm:items-baseline sm:gap-3";

export function QuoteCard({ details, totalPrice, extractedParams = [], time }: Props) {
  const [copied, setCopied] = useState(false);
  if (!details || details.length === 0) return null;
  const groups = groupByQuery(details);
  const paramGroups = groupByQuery(extractedParams);
  const multi = groups.length > 1;
  const when = fmtTime(time);
  const hasSteps = details.some((d) => d.calculation_steps && d.calculation_steps.length > 0);

  const copy = async () => {
    if (await copyWithToast(quoteToPlainText({ details, totalPrice }), "报价")) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section
      aria-label="报价单"
      className="w-full max-w-2xl overflow-hidden rounded-xl border border-warning/40 bg-card text-card-foreground shadow-[0_2px_8px_rgba(255,152,0,0.12)]"
    >
      {/* 抬头 */}
      <header className="flex items-center gap-3 border-b border-warning/30 bg-gradient-to-r from-warning-soft to-[#ffe0b2] px-4 py-3 md:px-5">
        <span aria-hidden className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-warning text-white">
          <ReceiptText className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-section leading-tight">报价单</h3>
          {when && <p className="text-caption tabular-nums text-muted-foreground">{when}</p>}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={copy}
          icon={copied ? <Check className="text-success" /> : <Copy />}
          aria-label="复制报价"
        >
          {copied ? "已复制" : "复制报价"}
        </Button>
      </header>

      {/* 列头 (桌面) */}
      <div
        aria-hidden
        className={cn(
          "hidden border-b bg-muted/50 px-4 py-2 text-caption font-medium text-muted-foreground md:px-5",
          COLS,
        )}
      >
        <span>品名</span>
        <span>规格</span>
        <span className="text-right">数量</span>
        <span className="text-right">单价</span>
        <span className="text-right">小计</span>
      </div>

      <div className="divide-y">
        {groups.map((g) => (
          <div key={g.queryIndex ?? "all"}>
            {g.queryIndex !== null && multi && (
              <div className="flex items-center justify-between gap-3 bg-muted/30 px-4 pb-1.5 pt-3 md:px-5">
                <span className="text-caption font-semibold text-foreground">询价 {g.queryIndex + 1}</span>
                <span className="text-caption text-muted-foreground">
                  小计 <span className="font-medium tabular-nums text-foreground">¥{fmt(groupSubtotal(g.items))}</span>
                </span>
              </div>
            )}
            <ul className="divide-y divide-dashed">
              {g.items.map((d, i) => (
                <DetailRow key={i} d={d} />
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* 合计 */}
      <div className="flex items-baseline justify-between gap-3 border-t-2 border-dashed px-4 py-3.5 md:px-5">
        <span className="text-sm font-medium text-muted-foreground">
          合计
          <span className="ml-1.5 text-caption">共 {details.length} 项</span>
        </span>
        <span className="text-2xl font-bold tabular-nums tracking-tight text-price">¥{fmt(totalPrice)}</span>
      </div>

      {(extractedParams.length > 0 || hasSteps) && (
        <div className="divide-y border-t bg-muted/20">
          {extractedParams.length > 0 && (
            <Collapsible icon={<ScanSearch />} title="识别参数">
              <div className="space-y-2.5">
                {paramGroups.map((g) => (
                  <div key={g.queryIndex ?? "all"} className="space-y-1.5">
                    {g.queryIndex !== null && paramGroups.length > 1 && (
                      <div className="text-caption font-medium text-foreground">询价 {g.queryIndex + 1}</div>
                    )}
                    {g.items.map((p, i) => (
                      <div key={i} className="text-caption leading-5">
                        <span className="font-medium text-foreground">
                          产品 {i + 1}: {productTypeName(p.product_type)}
                        </span>
                        <span className="text-muted-foreground"> · {paramSpec(p).join(" · ")}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Collapsible>
          )}
          {hasSteps && (
            <Collapsible icon={<Calculator />} title="计算过程">
              <div className="space-y-3">
                {groups.map((g) =>
                  g.items.map((d, i) =>
                    d.calculation_steps && d.calculation_steps.length > 0 ? (
                      <div key={`${g.queryIndex}-${i}`} className="space-y-1">
                        <div className="text-caption font-medium text-foreground">
                          {multi && g.queryIndex !== null && `询价 ${g.queryIndex + 1} · `}
                          {productTypeName(d.product_name)}
                          {d.applied_rule && <span className="font-normal text-muted-foreground"> · 规则: {d.applied_rule}</span>}
                        </div>
                        <ol className="space-y-0.5 border-l-2 border-primary/20 pl-3">
                          {d.calculation_steps.map((step, j) => (
                            <li key={j} className="break-words text-caption leading-5 text-muted-foreground">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : null,
                  ),
                )}
              </div>
            </Collapsible>
          )}
        </div>
      )}
    </section>
  );
}

function DetailRow({ d }: { d: CalcDetail }) {
  const spec = detailSpec(d);
  const qty = `${d.quantity || 0} ${quantityUnit(d)}`;
  return (
    <li className={cn("px-4 py-2.5 text-sm md:px-5", COLS)}>
      {/* 手机: 品名 + 小计一行, 规格 / 数量×单价第二行 */}
      <div className="flex items-baseline justify-between gap-3 sm:contents">
        <div className="min-w-0 font-medium">
          {productTypeName(d.product_name)}
          {d.double_sided && (
            <Badge variant="warning" className="ml-1.5 align-middle">
              双面 ×{fmt(d.double_sided_multiplier, 1)}
            </Badge>
          )}
        </div>
        <span className="hidden min-w-0 text-caption text-muted-foreground sm:block">{spec || "—"}</span>
        <span className="hidden text-right tabular-nums sm:block">{qty}</span>
        <span className="hidden text-right tabular-nums text-muted-foreground sm:block">¥{fmt(d.unit_price ?? 0)}</span>
        <span className="shrink-0 text-right font-semibold tabular-nums sm:order-none">¥{fmt(d.total_price ?? 0)}</span>
      </div>
      <div className="mt-0.5 text-caption text-muted-foreground sm:hidden">
        {[spec, `${qty} × ¥${fmt(d.unit_price ?? 0)}`].filter(Boolean).join(" · ")}
      </div>
    </li>
  );
}

function Collapsible({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <details className="group">
      <summary
        className={cn(
          "flex cursor-pointer select-none list-none items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground md:px-5",
          "hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "[&::-webkit-details-marker]:hidden [&_svg]:h-4 [&_svg]:w-4",
        )}
      >
        <span aria-hidden className="text-muted-foreground">{icon}</span>
        <span className="flex-1">{title}</span>
        <ChevronDown aria-hidden className="text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-4 pb-3.5 pt-0.5 md:px-5">{children}</div>
    </details>
  );
}
